import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, DollarSign, Users, Clock, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface VenueDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  section: 'venues' | 'bookings' | 'revenue' | 'schedule' | 'management' | 'analytics';
  data: any;
}

export const VenueDetailModal: React.FC<VenueDetailModalProps> = ({
  isOpen,
  onClose,
  section,
  data
}) => {
  const navigate = useNavigate();
  const renderContent = () => {
    switch (section) {
      case 'venues':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data?.venueUtilization?.map((venue: any, index: number) => (
                <Card key={index} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-white mb-2">{venue.name}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Utilization</span>
                        <span className={`font-semibold ${
                          venue.utilization >= 80 ? 'text-green-400' :
                          venue.utilization >= 50 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {venue.utilization}%
                        </span>
                      </div>
                      {venue.status && (
                        <Badge variant="outline" className="text-yellow-400">
                          {venue.status}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button onClick={() => { onClose(); navigate('/venue-management'); }}>
              Manage Venues
            </Button>
          </div>
        );

      case 'bookings':
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-white">{data?.todaysBookings || 0}</h3>
              <p className="text-gray-400">Total bookings today</p>
            </div>
            <div className="space-y-3">
              {data?.todaysSchedule?.map((booking: any) => (
                <Card key={booking.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-white">{booking.field}</h4>
                        <p className="text-gray-300">{booking.teams}</p>
                        <Badge variant="outline" className="mt-1">
                          {booking.type}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <span className="text-sports-green font-semibold">{booking.time}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button onClick={() => { onClose(); navigate('/venue-management'); }}>
              View Full Calendar
            </Button>
          </div>
        );

      case 'revenue':
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-white">
                ${data?.monthlyRevenue?.toLocaleString() || 0}
              </h3>
              <p className="text-gray-400">Monthly revenue</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-4 text-center">
                  <h4 className="text-lg font-semibold text-white">
                    ${((data?.monthlyRevenue || 0) / 30).toFixed(0)}
                  </h4>
                  <p className="text-gray-400">Daily Average</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-4 text-center">
                  <h4 className="text-lg font-semibold text-white">
                    ${((data?.monthlyRevenue || 0) * 12).toLocaleString()}
                  </h4>
                  <p className="text-gray-400">Yearly Projection</p>
                </CardContent>
              </Card>
            </div>
            <Button onClick={onClose} className="w-full">
              View Revenue Reports
            </Button>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              {data?.todaysSchedule?.map((item: any) => (
                <Card key={item.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-white">{item.field}</h4>
                        <p className="text-gray-300">{item.teams}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-sports-green font-semibold">{item.time}</span>
                        <Badge variant="outline" className="ml-2">
                          {item.type}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button onClick={() => { onClose(); navigate('/venue-management'); }}>
              Open Schedule Manager
            </Button>
          </div>
        );

      case 'management':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              {data?.venueUtilization?.map((venue: any, index: number) => (
                <Card key={index} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold text-white">{venue.name}</h4>
                        <p className="text-gray-300">Operations Status</p>
                      </div>
                      <div className="text-right">
                        <span className={`font-semibold ${
                          venue.utilization >= 80 ? 'text-green-400' :
                          venue.utilization >= 50 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {venue.utilization}% Utilization
                        </span>
                        {venue.status && (
                          <p className="text-sm text-yellow-400">{venue.status}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button onClick={() => { onClose(); navigate('/venue-management'); }}>
              Venue Settings
            </Button>
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-4 text-center">
                  <h4 className="text-2xl font-bold text-sports-green">
                    {data?.customerAnalytics?.regularCustomers || 0}
                  </h4>
                  <p className="text-gray-400">Regular Customers</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-4 text-center">
                  <h4 className="text-2xl font-bold text-sports-green">
                    {data?.customerAnalytics?.satisfactionRate || 0}%
                  </h4>
                  <p className="text-gray-400">Satisfaction Rate</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-4 text-center">
                  <h4 className="text-2xl font-bold text-sports-green">
                    {data?.customerAnalytics?.averageRating || 0}
                  </h4>
                  <p className="text-gray-400">Average Rating</p>
                </CardContent>
              </Card>
            </div>
            <Button onClick={() => { onClose(); navigate('/venue-management'); }}>
              Customer Insights Dashboard
            </Button>
          </div>
        );

      default:
        return <div>No data available</div>;
    }
  };

  const getTitle = () => {
    switch (section) {
      case 'venues': return 'Active Venues';
      case 'bookings': return "Today's Bookings";
      case 'revenue': return 'Monthly Revenue';
      case 'schedule': return "Today's Schedule";
      case 'management': return 'Venue Management';
      case 'analytics': return 'Customer Analytics';
      default: return 'Details';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">{getTitle()}</DialogTitle>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};